import { ElMessage } from "element-plus";
import { computed, MaybeRef, ref, unref } from "vue";
import {
  FormElementBase,
  FormElementType,
  FormResponse,
  FormResponseAnswer,
} from "~/api";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { clearErrors, showError } from "~/utils/handleAPIError";
import { getAnswerHasAnyValue } from "./rulesEngine";
import {
  AnswerValue,
  FormAnswerPlus,
  FormResponseAnswerMap,
  UpdateAnswerApiFunc,
  UpdateAnswerApiResult,
} from "./types";

/**
 * Debounce time in milliseconds for saving answers to prevent excessive API calls
 * This delay helps reduce server load when users are typing quickly
 */
const SET_ANSWER_DEBOUNCE = 500;

/**
 * Global map of timeout IDs for debounced answer submissions
 *
 * This map tracks all pending answer submission requests to:
 * 1. Allow cancellation of pending submissions when a new value is entered
 * 2. Prevent duplicate submissions for the same question
 * 3. Track in-flight requests across the application
 */
export const setAnswerTimeoutMap = ref(
  new Map<FormElementBase["questionCode"], NodeJS.Timeout>(),
);

/**
 * Configuration interface for the useAnswerWrapper composable
 */
interface UseAnswerArgs {
  /** The reactive map of all form answers */
  answers: MaybeRef<FormResponseAnswerMap>;

  /** The current form response being edited (optional if not submitting to API) */
  formResponse?: FormResponse;

  /**
   * Function to reload answers after API operations
   * @param answerList - Updated list of answers from the server
   * @param deletingGuid - Optional GUID of an answer that was deleted
   * @param deletingRepeaterGuid - Optional repeater GUID for a deleted answer
   */
  reloadAnswers: (
    answerList: FormResponseAnswer[],
    deletingGuid?: string,
    deletingRepeaterGuid?: string,
  ) => void;

  /** Function to refresh recently updated answers from the server */
  refreshRecentlyUpdatedAnswers: () => void;
}

/**
 * Creates a composable for managing form element answers with API integration
 *
 * This wrapper function creates a specialized useAnswer composable that:
 * 1. Provides a reactive reference to an answer value
 * 2. Handles local state updates
 * 3. Submits answer changes to the API with debouncing
 * 4. Supports custom API update functions for special cases
 *
 * @param {UseAnswerArgs} config - Configuration for the answer management
 * @returns A function that creates a reactive answer reference for a form element
 */
export function useAnswerWrapper({
  answers,
  formResponse,
  reloadAnswers,
  refreshRecentlyUpdatedAnswers,
}: UseAnswerArgs) {
  /**
   * Creates a reactive reference to an answer for a specific form element
   *
   * @template TAnswerType - The form element type, which determines the answer value type
   * @param {FormElementBase} el - The form element to create an answer for
   * @param {UpdateAnswerApiFunc<TAnswerType>} updateFunc - Optional custom API update function
   * @returns A computed ref that gets/sets the answer value with API integration
   */
  return function <TAnswerType extends FormElementType>(
    el: FormElementBase,
    updateFunc?: UpdateAnswerApiFunc<TAnswerType>,
  ) {
    /**
     * Updates an answer value locally and submits it to the API with debouncing
     *
     * This function:
     * 1. Updates the local answer state immediately
     * 2. Debounces API submissions to prevent excessive requests
     * 3. Handles both creating new answers and updating existing ones
     * 4. Provides user feedback on save/delete operations
     *
     * @param {AnswerValue<TAnswerType>} newVal - The new answer value to set
     * @param {FormElementBase} element - The form element being answered
     * @param {UpdateAnswerApiFunc<TAnswerType>} apiUpdateFunc - Optional custom API update function
     * @returns {AnswerValue<TAnswerType>} The new answer value (for chaining)
     */
    function setAnswer(
      newVal: AnswerValue<TAnswerType>,
      element: FormElementBase,
      apiUpdateFunc?: UpdateAnswerApiFunc<TAnswerType>,
    ): AnswerValue<TAnswerType> {
      // Get the existing answer for this element, if any
      const answerToSet: FormAnswerPlus<TAnswerType> = unref(answers)[
        element.questionCode
      ] as FormAnswerPlus<TAnswerType>;

      // Update the local answer state immediately
      if (!answerToSet) {
        // Create a new answer if one doesn't exist
        unref(answers)[element.questionCode] = {
          formElementGuid: element.guid,
          answerValue: newVal,
        };
      } else {
        // Update the existing answer
        unref(answers)[element.questionCode].answerValue = newVal;
      }

      // If no form response is provided, we're in local-only mode (no API submission)
      if (!formResponse) {
        return newVal;
      }

      // Cancel any pending API submission for this question
      clearTimeout(setAnswerTimeoutMap.value.get(element.questionCode));

      // Create a new debounced API submission
      const timeoutId = setTimeout(async () => {
        // Double-check form response exists (defensive programming)
        if (!formResponse) return;

        // Use the custom update function if provided, otherwise use the default
        const updateApi: UpdateAnswerApiFunc<TAnswerType> =
          apiUpdateFunc ?? defaultApiUpdateFunc;

        // Submit the answer to the API
        updateApi(newVal, element, formResponse)
          .then((res) => {
            // Provide user feedback based on the operation result
            switch (res.status) {
              case "updated":
                // Answer was successfully saved
                ElMessage.success({
                  message: "Saved",
                  grouping: true,
                });
                if (import.meta.env.DEV) {
                  console.log("Saved", element.questionCode);
                }
                break;
              case "deleted":
                // Answer was successfully deleted
                ElMessage.warning({
                  message: "Removed",
                  grouping: true,
                });
                if (import.meta.env.DEV) {
                  console.warn("Removed", element.questionCode);
                }
                break;
            }

            // For custom update functions, refresh all recently updated answers
            // This ensures computed questions and other dependent values are updated
            if (apiUpdateFunc) {
              refreshRecentlyUpdatedAnswers();
            }
          })
          .catch(showError);

        // Remove this request from the timeout map once completed
        setAnswerTimeoutMap.value.delete(element.questionCode);
      }, SET_ANSWER_DEBOUNCE);

      // Store the timeout ID in the map for potential cancellation
      setAnswerTimeoutMap.value.set(element.questionCode, timeoutId);

      // Return the new value immediately (don't wait for API)
      return newVal;
    }

    /**
     * Default implementation for submitting answers to the API
     *
     * This function:
     * 1. Determines whether to create/update or delete an answer based on its value
     * 2. Makes the appropriate API call
     * 3. Updates the local answer state with the server response
     * 4. Handles errors and provides appropriate return values
     *
     * @param {AnswerValue<TAnswerType> | null} newVal - The new answer value to submit
     * @param {FormElementBase} element - The form element being answered
     * @param {FormResponse} response - The form response being edited
     * @returns {Promise<UpdateAnswerApiResult<TAnswerType>>} Result of the API operation
     */
    async function defaultApiUpdateFunc(
      newVal: AnswerValue<TAnswerType> | null,
      element: FormElementBase,
      response: FormResponse,
    ): Promise<UpdateAnswerApiResult<TAnswerType>> {
      // Clear any previous API errors
      clearErrors();

      // If the answer has a value, create or update it
      if (getAnswerHasAnyValue(newVal, element.type)) {
        try {
          // Submit the answer to the API
          const updatedAnswerList =
            await apiClient.formResponseAnswer.submitAnswer(
              response.guid,
              element.guid,
              element.repeaterGuid ?? emptyGuid,
              element.questionCode ?? "",
              response.formDefinitionGuid,
              newVal,
            );

          // Update all answers with the server response
          // This ensures computed questions and dependent values are updated
          reloadAnswers(updatedAnswerList);

          // Return success status with the submitted value
          return {
            status: "updated",
            value: newVal,
          };
        } catch (error) {
          // Handle and display any API errors
          showError(error);
          return {
            status: "error",
            value: null,
          };
        }
      } else {
        // If the answer has no value, delete it
        try {
          // Delete the answer via the API
          const updatedAnswerList =
            await apiClient.formResponseAnswer.deleteAnswer(
              response.formDefinitionGuid,
              response.guid,
              element.guid,
              element.repeaterGuid ?? emptyGuid,
            );

          // Update the answers map, specifying which answer was deleted
          // This ensures it's properly removed from the local state
          reloadAnswers(
            updatedAnswerList,
            element.guid,
            element.repeaterGuid ?? emptyGuid,
          );

          // Return deleted status
          return {
            status: "deleted",
            value: null,
          };
        } catch (error) {
          // Handle and display any API errors
          showError(error);
          return {
            status: "error",
            value: null,
          };
        }
      }
    }

    /**
     * Create a computed property that provides a reactive interface to the answer
     *
     * The computed property:
     * - Getter: Retrieves the current answer value from the answers map
     * - Setter: Updates the answer value and submits it to the API
     *
     * This allows for simple v-model binding in templates
     */
    const res = computed<any>({
      get: () => {
        // Create the composite key using question code and repeater GUID
        const repeatableGroupGuid = el.repeaterGuid ?? emptyGuid;
        const key = el.questionCode + "-" + repeatableGroupGuid;

        // Return the current answer value from the answers map
        return unref(answers)[key]?.answerValue;
      },
      set: (newVal: AnswerValue<TAnswerType>) =>
        // When the value is set, update it locally and submit to API
        setAnswer(newVal, el, updateFunc),
    });

    return res;
  };
}
